import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { useT } from '@/i18n/use-t';
import type { ScreenEvent } from './screen-flow-types';

export type ScreenNodeData = {
  label: string;
  screenId: string;
  isStart: boolean;
  isTerminal: boolean;
  events: ScreenEvent[];
};

export type ScreenFlowRfNode = Node<ScreenNodeData, 'screen'>;

const HEADER_H = 46;
const ROW_H = 22;

/**
 * RFL-142 — a journey node = one terminal screen. A target handle on the
 * left (unless it's a start screen) and one source handle per declared
 * event on the right, so an operator wires "on <event> → next screen".
 */
export function ScreenNode({ data, selected }: NodeProps<ScreenFlowRfNode>) {
  const t = useT('admin_ui');
  return (
    <div
      className={`rounded-md border bg-card text-xs shadow-sm ${
        selected ? 'border-primary' : 'border-border'
      }`}
      style={{ width: 200 }}
    >
      {!data.isStart && <Handle type="target" position={Position.Left} />}

      <div className="border-b border-border px-3 py-2">
        <div className="font-medium">{data.label}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{data.screenId}</div>
        {data.isStart && (
          <span className="text-[10px] text-sky-500">{t('screenFlows.startBadge', 'start')}</span>
        )}
        {data.isTerminal && (
          <span className="text-[10px] text-emerald-500">{t('screenFlows.endBadge', 'end')}</span>
        )}
      </div>

      <div className="py-1">
        {data.events.length === 0 ? (
          <div className="px-3 py-1 text-[10px] text-muted-foreground">
            {t('screenFlows.node.noEvents', 'no events')}
          </div>
        ) : (
          data.events.map((ev, i) => (
            <div key={ev.name} className="relative px-3 py-0.5 text-right text-[11px]">
              {ev.label ?? ev.name}
              <Handle
                type="source"
                id={ev.name}
                position={Position.Right}
                style={{ top: HEADER_H + i * ROW_H + ROW_H / 2 }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
